import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  BuildingOffice2Icon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon,
  UserIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  Squares2X2Icon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { API_URL } from '../api/config';
import { COUNTRIES } from '../constants/countries';

const MODULE_OPTIONS: [string, string, string][] = [
  ['activities', 'Activities', 'Plan, assign, report, and review field work'],
  ['cohorts', 'Cohorts', 'Optional farmer grouping and programme cohorts'],
  ['crops', 'Crops', 'Manage crops, varieties, seasons, and crop coverage'],
  ['farmers', 'Farmers', 'Manage farmer profiles and records'],
  ['field_diary', 'Field Diary', 'Daily field notes and scout diaries'],
  ['issues', 'Field Issues', 'Report, assign, and resolve field problems'],
  ['traceability', 'Traceability', 'Tree and crop tracking'],
  ['training', 'Training', 'Farmer education and courses'],
  ['maps', 'Farm Maps', 'Required farm boundaries for farmer operations'],
  ['compost', 'Compost', 'Tenant-specific compost and fertilizer records'],
  ['warehouse', 'Warehouse', 'Storage and inventory'],
  ['orders', 'Orders', 'Buyer order management'],
  ['vsla', 'Finance / VSLA', 'Savings and loan groups'],
  ['marketplace', 'Marketplace', 'Buyer-seller connections'],
];

const BUSINESS_TYPES = [
  'Agribusiness',
  'Cooperative',
  'NGO / Non-profit',
  'Government',
  'Aggregator',
  'Input Supplier',
  'Other',
];

const CROP_OPTIONS = [
  'Avocado',
  'Coffee',
  'Tea',
  'Maize',
  'Beans',
  'Cassava',
  'Banana',
  'Tomato',
  'Mixed crops',
  'Other',
];

type Step = 1 | 2 | 3;

const TenantRegisterPage: React.FC = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState<Step>(1);

  const [form, setForm] = useState({
    company_name: '',
    country: 'Rwanda',
    country_code: 'RW',
    business_type: '',
    main_crops: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    requested_modules: ['farmers', 'traceability', 'activities'],
    registration_number: '',
    tin: '',
    website_url: '',
    province: '',
    district: '',
    sector: '',
    cell: '',
    village: '',
    operating_districts: '',
    legal_name: '',
    domain: '',
    admin_domain: '',
    farmer_count: '',
    notes: '',
  });

  useEffect(() => {
    const loadInvite = async () => {
      try {
        const res = await fetch(`${API_URL}/api/tenant-registration/${token}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Invalid registration link');
        const req = data.request || {};
        setForm((prev) => ({
          ...prev,
          company_name: req.company_name || prev.company_name,
          contact_name: req.contact_name || prev.contact_name,
          contact_email: req.contact_email || prev.contact_email,
          contact_phone: req.contact_phone || prev.contact_phone,
          requested_modules: Array.isArray(req.requested_modules) && req.requested_modules.length ? req.requested_modules : prev.requested_modules,
        }));
      } catch (err: any) {
        setError(err.message || 'Failed to load registration invite');
      } finally {
        setLoading(false);
      }
    };
    loadInvite();
  }, [token]);

  const update = (key: string, value: any) => setForm((prev) => ({ ...prev, [key]: value }));

  const toggleModule = (key: string) => {
    setForm((prev) => ({
      ...prev,
      requested_modules: prev.requested_modules.includes(key)
        ? prev.requested_modules.filter((item) => item !== key)
        : [...prev.requested_modules, key],
    }));
  };

  const toggleCrop = (crop: string) => {
    const current = form.main_crops ? form.main_crops.split(', ') : [];
    const next = current.includes(crop)
      ? current.filter((c) => c !== crop)
      : [...current, crop];
    update('main_crops', next.join(', '));
  };

  const selectedCrops = form.main_crops ? form.main_crops.split(', ').filter(Boolean) : [];

  const canProceedStep1 = form.company_name.trim() && form.contact_name.trim() && form.contact_email.trim();

  const submit = async () => {
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/tenant-registration/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to submit registration');
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to submit registration');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-950 text-white">Loading registration...</div>;
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 p-4">
        <div className="max-w-lg rounded-3xl bg-white p-8 text-center shadow-2xl">
          <CheckCircleIcon className="mx-auto h-16 w-16 text-emerald-600" />
          <h1 className="mt-4 text-3xl font-black text-slate-950">Submitted</h1>
          <p className="mt-3 text-slate-600">Your organisation registration is waiting for Super Admin approval. Once approved, you can sign in with a verification code sent to your email or phone.</p>
          <Link to="/login" className="mt-6 inline-flex rounded-2xl bg-slate-950 px-6 py-3 text-sm font-black text-white">Go to Login</Link>
        </div>
      </div>
    );
  }

  const stepLabels = ['Organisation Identity', 'Platform Needs', 'Invoicing Details'];

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="mx-auto grid min-h-screen max-w-7xl lg:grid-cols-[0.9fr_1.4fr]">
        <aside className="flex flex-col justify-between bg-gradient-to-br from-emerald-900 via-slate-950 to-slate-950 p-8 text-white">
          <div>
            <div className="inline-flex rounded-2xl bg-white/10 p-4">
              <BuildingOffice2Icon className="h-10 w-10" />
            </div>
            <h1 className="mt-8 text-4xl font-black leading-tight">Organisation onboarding</h1>
            <p className="mt-4 max-w-md text-slate-300">A few details to get started. You can complete your full organisation profile later in Settings.</p>
          </div>

          <div className="mt-8 space-y-3">
            {stepLabels.map((label, i) => (
              <div key={i} className={`flex items-center gap-3 rounded-2xl border p-4 transition ${
                step === i + 1 ? 'border-emerald-500 bg-white/10' : step > i + 1 ? 'border-emerald-500/30 bg-white/5' : 'border-white/10 bg-white/5'
              }`}>
                <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-black ${
                  step > i + 1 ? 'bg-emerald-500 text-slate-950' : step === i + 1 ? 'bg-white text-slate-950' : 'bg-white/10 text-white'
                }`}>
                  {step > i + 1 ? '✓' : i + 1}
                </div>
                <span className={`text-sm font-bold ${step === i + 1 ? 'text-white' : 'text-slate-400'}`}>{label}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 rounded-3xl border border-white/10 bg-white/5 p-5">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-emerald-300">Quick start</p>
            <p className="mt-2 text-sm text-slate-300">Only organisation name, country, and contact info are required. You can add registration details, tax numbers, and operating regions later.</p>
          </div>
        </aside>

        <main className="bg-slate-50 p-4 sm:p-8">
          <div className="mx-auto max-w-4xl space-y-5">
            <div className="rounded-3xl bg-white p-6 shadow-sm">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-emerald-600">Step {step} of 3</p>
              <h2 className="mt-2 text-3xl font-black text-slate-950">{stepLabels[step - 1]}</h2>
              {error && <div className="mt-4 rounded-2xl bg-red-50 p-4 text-sm font-bold text-red-700">{error}</div>}
            </div>

            {step === 1 && (
              <>
                <section className="rounded-3xl bg-white p-6 shadow-sm">
                  <div className="mb-4 flex items-center gap-3">
                    <ClipboardDocumentCheckIcon className="h-6 w-6 text-emerald-700" />
                    <h3 className="font-black text-slate-950">Organisation <span className="text-xs font-normal text-slate-400">(required fields marked *)</span></h3>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <input required value={form.company_name} onChange={(e) => update('company_name', e.target.value)}
                      placeholder="Organisation name *" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-500" />
                    <select value={form.country_code} onChange={(e) => {
                      const country = COUNTRIES.find(c => c.code === e.target.value);
                      update('country_code', e.target.value);
                      update('country', country?.name || '');
                    }}
                      className="rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-500">
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                    <select value={form.business_type} onChange={(e) => update('business_type', e.target.value)}
                      className="rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-500">
                      <option value="">Business type</option>
                      {BUSINESS_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    <div className="md:col-span-2">
                      <p className="mb-2 text-xs font-bold text-slate-500">Main crops / value chains</p>
                      <div className="flex flex-wrap gap-2">
                        {CROP_OPTIONS.map((crop) => (
                          <label key={crop} className={`flex items-center gap-2 rounded-xl border-2 px-3 py-2 text-sm font-semibold transition cursor-pointer ${selectedCrops.includes(crop) ? 'border-emerald-500 bg-emerald-50 text-emerald-900' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                            <input type="checkbox" checked={selectedCrops.includes(crop)} onChange={() => toggleCrop(crop)} className="h-4 w-4 accent-emerald-600" />
                            {crop}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </section>

                <section className="rounded-3xl bg-white p-6 shadow-sm">
                  <div className="mb-4 flex items-center gap-3">
                    <UserIcon className="h-6 w-6 text-emerald-700" />
                    <h3 className="font-black text-slate-950">Your Details</h3>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <input required value={form.contact_name} onChange={(e) => update('contact_name', e.target.value)} placeholder="Full name *" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-500" />
                    <div className="relative">
                      <EnvelopeIcon className="absolute left-4 top-3.5 h-5 w-5 text-slate-400" />
                      <input required type="email" value={form.contact_email} onChange={(e) => update('contact_email', e.target.value)} placeholder="Email *" className="w-full rounded-2xl border border-slate-200 py-3 pl-12 pr-4 text-sm outline-none focus:border-emerald-500" />
                    </div>
                    <div className="relative">
                      <PhoneIcon className="absolute left-4 top-3.5 h-5 w-5 text-slate-400" />
                      <input value={form.contact_phone} onChange={(e) => update('contact_phone', e.target.value)} placeholder="Phone (optional)" className="w-full rounded-2xl border border-slate-200 py-3 pl-12 pr-4 text-sm outline-none focus:border-emerald-500" />
                    </div>
                  </div>
                </section>

                <section className="rounded-3xl bg-white p-6 shadow-sm">
                  <div className="mb-4 flex items-center gap-3">
                    <DocumentTextIcon className="h-6 w-6 text-emerald-700" />
                    <h3 className="font-black text-slate-950">Invoicing Details <span className="text-xs font-normal text-slate-400">(optional)</span></h3>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <input value={form.tin} onChange={(e) => update('tin', e.target.value)}
                        placeholder="TIN / Tax identification number" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-500" />
                      <p className="mt-1.5 text-xs text-slate-500">Optional. Used for invoicing and partner verification.</p>
                    </div>
                    <div>
                      <input value={form.registration_number} onChange={(e) => update('registration_number', e.target.value)}
                        placeholder="Business registration number" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-500" />
                      <p className="mt-1.5 text-xs text-slate-500">Optional. If your organisation is legally registered.</p>
                    </div>
                  </div>
                </section>

                <section className="rounded-3xl border border-emerald-100 bg-emerald-50 p-6 shadow-sm">
                  <h3 className="font-black text-emerald-950">Passwordless access</h3>
                  <p className="mt-2 text-sm leading-6 text-emerald-800">
                    No password is required here. After approval, your Project Manager account will use a one-time verification code sent to your email or phone.
                  </p>
                </section>
              </>
            )}

            {step === 2 && (
              <section className="rounded-3xl bg-white p-6 shadow-sm">
                <div className="mb-2 flex items-center gap-3">
                  <Squares2X2Icon className="h-6 w-6 text-emerald-700" />
                  <h3 className="font-black text-slate-950">What do you want to manage?</h3>
                </div>
                <p className="mb-5 text-sm text-slate-500">Select the modules your organisation needs. You can enable or disable these later.</p>
                <div className="grid gap-3 md:grid-cols-2">
                  {MODULE_OPTIONS.map(([key, label, desc]) => (
                    <label key={key} className={`flex items-start gap-4 rounded-2xl border-2 px-5 py-4 transition cursor-pointer ${
                      form.requested_modules.includes(key)
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-100 hover:border-slate-200'
                    }`}>
                      <input type="checkbox" checked={form.requested_modules.includes(key)} onChange={() => toggleModule(key)}
                        className="mt-1 h-5 w-5 shrink-0 accent-emerald-600" />
                      <div>
                        <p className="text-sm font-black text-slate-950">{label}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </section>
            )}

            {step === 3 && (
              <>
                <section className="rounded-3xl bg-white p-6 shadow-sm">
                  <div className="mb-4 flex items-center gap-3">
                    <DocumentTextIcon className="h-6 w-6 text-emerald-700" />
                    <h3 className="font-black text-slate-950">Organisation Registration</h3>
                  </div>
                  <p className="mb-4 text-sm text-slate-500">Optional. You can fill these in later from Organisation Settings.</p>
                  <div className="grid gap-4 md:grid-cols-2">
                    <input value={form.legal_name} onChange={(e) => update('legal_name', e.target.value)}
                      placeholder="Legal registered name" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-500" />
                    <input value={form.website_url} onChange={(e) => update('website_url', e.target.value)}
                      placeholder="Website URL" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-500" />
                  </div>
                </section>

                <section className="rounded-3xl bg-white p-6 shadow-sm">
                  <div className="mb-4 flex items-center gap-3">
                    <MapPinIcon className="h-6 w-6 text-emerald-700" />
                    <h3 className="font-black text-slate-950">Operating Region</h3>
                  </div>
                  <div className="mb-4 flex items-center gap-2 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    <span className="font-black text-slate-900">Primary country:</span>
                    <span>{COUNTRIES.find((c) => c.code === form.country_code)?.name || form.country}</span>
                  </div>
                  <div className="grid gap-4 md:grid-cols-3">
                    {['province', 'district', 'sector', 'cell', 'village'].map((key) => (
                      <input key={key} value={(form as any)[key]} onChange={(e) => update(key, e.target.value)}
                        placeholder={key.replace('_', ' ')} className="rounded-2xl border border-slate-200 px-4 py-3 text-sm capitalize outline-none focus:border-emerald-500" />
                    ))}
                    <input value={form.farmer_count} onChange={(e) => update('farmer_count', e.target.value)}
                      placeholder="Estimated farmers" className="rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-500" />
                    <textarea value={form.operating_districts} onChange={(e) => update('operating_districts', e.target.value)}
                      placeholder="Operating regions / districts (comma-separated, e.g. Gasabo, Kayonza)" className="min-h-24 rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-500 md:col-span-3" />
                  </div>
                  <div className="mt-3">
                    <p className="mb-2 text-xs font-bold text-slate-500">Quick add common Rwandan districts</p>
                    <div className="flex flex-wrap gap-2">
                      {['Gasabo', 'Kicukiro', 'Nyarugenge', 'Bugesera', 'Kayonza', 'Huye', 'Musanze', 'Rubavu', 'Nyagatare', 'Rusizi'].map((d) => {
                        const active = form.operating_districts.split(',').map((x) => x.trim()).includes(d);
                        return (
                          <button key={d} type="button" onClick={() => {
                            const current = form.operating_districts.split(',').map((x) => x.trim()).filter(Boolean);
                            const next = active ? current.filter((x) => x !== d) : [...current, d];
                            update('operating_districts', next.join(', '));
                          }}
                            className={`rounded-xl border-2 px-3 py-1.5 text-sm font-semibold transition cursor-pointer ${active ? 'border-emerald-500 bg-emerald-50 text-emerald-900' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                            {d}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </section>

                <section className="rounded-3xl border-2 border-slate-200 bg-slate-50 p-6 shadow-sm">
                  <div className="mb-4 flex items-center gap-3">
                    <ClipboardDocumentCheckIcon className="h-6 w-6 text-emerald-700" />
                    <h3 className="font-black text-slate-950">Review your invoicing details</h3>
                  </div>
                  <div className="grid gap-3 text-sm md:grid-cols-2">
                    <div className="rounded-2xl bg-white p-4 border border-slate-100">
                      <p className="text-xs font-bold text-slate-400 uppercase">TIN / Tax identification number</p>
                      <p className="mt-1 font-bold text-slate-900">{form.tin || 'Not provided yet'}</p>
                    </div>
                    <div className="rounded-2xl bg-white p-4 border border-slate-100">
                      <p className="text-xs font-bold text-slate-400 uppercase">Business registration number</p>
                      <p className="mt-1 font-bold text-slate-900">{form.registration_number || 'Not provided yet'}</p>
                    </div>
                    <div className="rounded-2xl bg-white p-4 border border-slate-100">
                      <p className="text-xs font-bold text-slate-400 uppercase">Legal registered name</p>
                      <p className="mt-1 font-bold text-slate-900">{form.legal_name || 'Not provided yet'}</p>
                    </div>
                    <div className="rounded-2xl bg-white p-4 border border-slate-100">
                      <p className="text-xs font-bold text-slate-400 uppercase">Operating regions</p>
                      <p className="mt-1 font-bold text-slate-900">{form.operating_districts || 'Not provided yet'}</p>
                    </div>
                  </div>
                </section>

                <section className="rounded-3xl bg-white p-6 shadow-sm">
                  <h3 className="font-black text-slate-950">Notes for Super Admin</h3>
                  <textarea value={form.notes} onChange={(e) => update('notes', e.target.value)}
                    placeholder="Any additional information for the platform team..." className="mt-3 min-h-24 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-emerald-500" />
                </section>
              </>
            )}

            <div className="flex items-center gap-3">
              {step > 1 && (
                <button onClick={() => setStep((step - 1) as Step)}
                  className="flex items-center gap-2 rounded-2xl border-2 border-slate-200 bg-white px-6 py-3 text-sm font-black text-slate-700 hover:bg-slate-50">
                  <ChevronLeftIcon className="h-4 w-4" />
                  Back
                </button>
              )}
              <div className="flex-1" />
              {step < 3 ? (
                <button onClick={() => {
                  if (step === 1 && !canProceedStep1) {
                    setError('Please fill in organisation name, your name, and email.');
                    return;
                  }
                  setError('');
                  setStep((step + 1) as Step);
                }}
                  className="flex items-center gap-2 rounded-2xl bg-emerald-600 px-8 py-3 text-sm font-black text-white shadow-lg hover:bg-emerald-700">
                  Continue
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              ) : (
                <button onClick={submit} disabled={submitting}
                  className="flex items-center gap-2 rounded-2xl bg-emerald-600 px-8 py-3 text-sm font-black text-white shadow-lg hover:bg-emerald-700 disabled:opacity-50">
                  {submitting ? 'Submitting...' : 'Submit Registration'}
                </button>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default TenantRegisterPage;
